import React, { useMemo } from 'react';
import { DatePicker, Input, InputNumber, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import type { FilterConfig } from './FilterBar';

interface BaseFilterOpts {
  key: string;
  label: string;
  placeholder?: string;
  width?: number;
}

export interface SearchFilterOpts extends BaseFilterOpts {
  value: string;
  onChange: (value: string) => void;
  onSearch?: () => void;
}

export function createSearchFilter(o: SearchFilterOpts): FilterConfig {
  return {
    key: o.key,
    label: o.label,
    type: 'text',
    render: () => (
      <Input.Search
        placeholder={o.placeholder}
        value={o.value}
        allowClear
        style={{ width: o.width ?? 220 }}
        onChange={(e) => o.onChange(e.target.value)}
        onSearch={o.onSearch}
      />
    ),
  };
}

export interface SelectFilterOpts extends BaseFilterOpts {
  value: string | undefined;
  onChange: (value: string) => void;
  options: { label: string; value: string }[];
}

export function createSelectFilter(o: SelectFilterOpts): FilterConfig {
  return {
    key: o.key,
    label: o.label,
    type: 'select',
    render: () => (
      <Select
        placeholder={o.placeholder ?? o.label}
        allowClear
        style={{ width: o.width ?? 130 }}
        value={o.value || undefined}
        options={o.options}
        onChange={(v) => o.onChange(v ?? '')}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  };
}

export interface NumberFilterOpts extends BaseFilterOpts {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
}

export function createNumberFilter(o: NumberFilterOpts): FilterConfig {
  return {
    key: o.key,
    label: o.label,
    type: 'number',
    render: () => (
      <InputNumber
        placeholder={o.placeholder ?? o.label}
        value={o.value}
        min={o.min}
        max={o.max}
        style={{ width: o.width ?? 120 }}
        onChange={(v) => o.onChange(v ?? undefined)}
      />
    ),
  };
}

export interface DateRangeFilterOpts extends BaseFilterOpts {
  value: [Dayjs | null, Dayjs | null] | null;
  onChange: (range: [Dayjs | null, Dayjs | null] | null) => void;
}

// SafeRangePicker giúp memoize tham chiếu đối tượng Dayjs dựa trên valueOf.
// Điều này ngăn chặn lỗi "Maximum update depth exceeded" của Antd RangePicker khi component cha re-render liên tục.
const SafeRangePicker = ({
  value,
  onChange,
  style,
}: {
  value: [Dayjs | null, Dayjs | null] | null;
  onChange: (range: [Dayjs | null, Dayjs | null] | null) => void;
  style?: React.CSSProperties;
}) => {
  const memoizedValue = useMemo(() => {
    if (!value) return null;
    return [value[0], value[1]] as [Dayjs | null, Dayjs | null];
  }, [value?.[0]?.valueOf(), value?.[1]?.valueOf()]);

  return (
    <DatePicker.RangePicker
      style={style}
      value={memoizedValue as [Dayjs, Dayjs] | null}
      onChange={(v) => onChange(v as [Dayjs | null, Dayjs | null] | null)}
    />
  );
};

export function createDateRangeFilter(o: DateRangeFilterOpts): FilterConfig {
  return {
    key: o.key,
    label: o.label,
    type: 'daterange',
    render: () => (
      <SafeRangePicker
        style={{ width: o.width ?? 240 }}
        value={o.value}
        onChange={o.onChange}
      />
    ),
  };
}

